import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ImageIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { fadeInUp } from '@/lib/motion';

export default function OrgChart() {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchOrgChart() {
      const { data } = await supabase
        .from('org_chart_settings_2026_04_15')
        .select('image_url')
        .eq('id', 1)
        .single();
      if (data) {
        setImageUrl(data.image_url ?? null);
      }
      setIsLoading(false);
    }
    fetchOrgChart();
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-background/90 backdrop-blur-md border-b border-border px-4 py-4 sticky top-0 z-30">
        <div className="container mx-auto flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <img
              src="/images/hkcmprgu-logo-01.png"
              alt="香港中醫師權益總工會"
              className="h-10 w-auto"
            />
            <span className="text-lg font-bold text-primary hidden sm:inline">
              香港中醫師權益總工會
            </span>
          </Link>
          <Link to={ROUTE_PATHS.HOME}>
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              返回主頁
            </Button>
          </Link>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 py-12 max-w-5xl">
        <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
          {/* Page Title */}
          <div className="text-center mb-10">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
              第七屆理事會架構圖
            </h1>
            <div className="mt-3 w-16 h-1 bg-primary mx-auto rounded-full" />
          </div>

          {/* Org Chart Image */}
          {isLoading ? (
            <div className="flex justify-center items-center py-32">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : imageUrl ? (
            <div className="w-full rounded-2xl overflow-hidden shadow-lg border border-border">
              <img
                src={imageUrl}
                alt="第七屆理事會架構圖"
                className="w-full h-auto block"
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
              <div className="relative mb-6">
                <div className="w-16 h-16 border-4 border-primary/20 rounded-full flex items-center justify-center">
                  <ImageIcon className="w-7 h-7 opacity-40" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-primary/20 rounded-full animate-pulse" />
              </div>
              <p className="text-xl font-semibold text-foreground">更新中</p>
              <p className="text-sm mt-2 text-muted-foreground">架構圖正在更新，請稍後再查看</p>
            </div>
          )}
        </motion.div>
      </main>

      <footer className="py-6 text-center text-sm text-muted-foreground border-t border-border">
        © 2026 香港中醫師權益總工會。保留所有權利。
      </footer>
    </div>
  );
}
